import config from '../../../config';

import MemberStep from './components/MemberStep';
import ComponentStep from './components/ComponentStep';
import ModelStep from './components/ModelStep';
import SourceStep from './components/SourceStep';
import VariableStep from './components/VariableStep';

const STEP_DEFINITIONS = {
  member: {
    component: MemberStep,
    title: 'Add site members',
    name: 'member',
    icon: 'he-user',
    cardLabel: 'Add site members',
  },
  model: {
    component: ModelStep,
    title: 'Upload 3D & 2D models',
    name: 'model',
    icon: 'he-cube',
    cardLabel: 'Upload 3D & 2D models',
    showsLoading: true,
  },
  component: {
    component: ComponentStep,
    title: 'Set up component tree',
    name: 'component',
    icon: 'he-schema',
    cardLabel: 'Set up component tree',
  },
  source: {
    component: SourceStep,
    title: 'Add data sources',
    name: 'source',
    icon: 'he-storage',
    cardLabel: 'Add data sources',
  },
  variable: {
    component: VariableStep,
    title: 'Visualize signals',
    name: 'variable',
    icon: 'he-line-chart-alt',
    cardLabel: 'Visualize signals',
    requiresSources: true,
  },
};

const WIZARD_DEFINITIONS = {
  basic: {
    name: 'basic',
    label: 'Basic',
    theme: 'basic',
    description: 'Essential content for IdentiQ starter package',
    stepNames: ['member', 'model', 'component'],
  },
  advanced: {
    name: 'advanced',
    label: 'Advanced',
    theme: 'advanced',
    description: 'Essential content with data for IdentiQ professional package',
    stepNames: ['member', 'model', 'component', 'source', 'variable'],
  },
  onprem: {
    name: 'onprem',
    label: 'On-prem',
    theme: 'basic',
    description: 'Essential content with data for IdentiQ on-premises solution',
    stepNames: ['component', 'variable'],
  },
};

const getStepNames = (wizardType) => getWizardDefinition(wizardType)?.stepNames || [];

const getStoredCompletedSteps = (site, wizardType) =>
  site?.creationState?.find(({ name }) => name === wizardType)?.steps || [];

const getSynchronizedWizardTypes = (wizardType) => {
  const availableWizardTypes = getAvailableWizardTypes();

  return availableWizardTypes.length > 1 && availableWizardTypes.includes(wizardType)
    ? availableWizardTypes
    : [wizardType];
};

const getSharedStepNames = (wizardTypes) => {
  if (wizardTypes.length < 2) {
    return new Set();
  }

  const [firstWizardType] = wizardTypes;
  const firstStepNames = getStepNames(firstWizardType);

  return new Set(
    firstStepNames.filter((stepName) =>
      wizardTypes.every((candidateWizardType) =>
        getStepNames(candidateWizardType).includes(stepName)
      )
    )
  );
};

export const getAvailableWizardTypes = () =>
  config.onPremise ? ['onprem'] : ['basic', 'advanced'];

export const getWizardDefinition = (wizardType) => WIZARD_DEFINITIONS[wizardType] || null;

export const getWizardTheme = (wizardType) => getWizardDefinition(wizardType)?.theme || null;

export const getWizardHeaderTitle = (wizardType) => {
  if (wizardType === 'onprem') {
    return null;
  }

  return getWizardDefinition(wizardType)?.label || null;
};

export const getWizardSteps = (wizardType) => {
  const stepNames = getStepNames(wizardType);
  return stepNames.map((stepName) => STEP_DEFINITIONS[stepName]);
};

export const getWizardCards = () =>
  getAvailableWizardTypes().map((wizardType) => {
    const wizardDefinition = getWizardDefinition(wizardType);

    return {
      ...wizardDefinition,
      cardSteps: wizardDefinition.stepNames.map((stepName) => STEP_DEFINITIONS[stepName]),
    };
  });

export const getCompletedSteps = (site, wizardType) => {
  const stepNames = getStepNames(wizardType);

  if (!site?.creationState || !wizardType || stepNames.length === 0) {
    return [];
  }

  const synchronizedWizardTypes = getSynchronizedWizardTypes(wizardType);
  const sharedStepNames = getSharedStepNames(synchronizedWizardTypes);
  const completedStepsByName = new Map();

  synchronizedWizardTypes.forEach((candidateWizardType) => {
    getStoredCompletedSteps(site, candidateWizardType).forEach((step) => {
      if (!stepNames.includes(step.name)) {
        return;
      }

      if (candidateWizardType !== wizardType && !sharedStepNames.has(step.name)) {
        return;
      }

      if (!completedStepsByName.has(step.name)) {
        completedStepsByName.set(step.name, step);
      }
    });
  });

  return stepNames
    .filter((stepName) => completedStepsByName.has(stepName))
    .map((stepName) => completedStepsByName.get(stepName));
};

export const isWizardStepComplete = (site, wizardType, stepName) =>
  getCompletedSteps(site, wizardType).some((step) => step.name === stepName);

export const wizardStepRequiresSources = (stepName) =>
  Boolean(STEP_DEFINITIONS[stepName]?.requiresSources);

export const wizardStepShowsLoading = (stepName) =>
  Boolean(STEP_DEFINITIONS[stepName]?.showsLoading);

export const hasAvailableWizardStep = (stepName) =>
  getAvailableWizardTypes().some((wizardType) =>
    getWizardSteps(wizardType).some((wizardStep) => wizardStep.name === stepName)
  );
